import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { UploadType } from 'services/api/uploads'
import * as yup from 'yup'
import { BrandState, updateBrandRequest } from 'state/brands'
import { useNewTranslation } from 'hooks'
import { createBrand } from 'state/teams'
import { TeamState } from 'state/teams'
import { MODAL_TYPE } from '.'
import { selectCountryList, selectIndustryList, selectMemberRange } from 'state/constants'
import { getStringLength } from 'components/utils'
import Modal from './index'

const CreateBrand: React.FC<any> = ({
    type,
    onClose,
    team,
    brand,
    className,
    onUpdateSuccess,
}: {
    type: MODAL_TYPE
    onClose: (closeOnSubmit?: boolean) => void
    team: TeamState
    brand: BrandState
    className: string
    onUpdateSuccess?: () => void
}) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const countries = useSelector(selectCountryList)
    const industryOptions = useSelector(selectIndustryList)
    const memberRangeOptions = useSelector(selectMemberRange)
    const isCreateMode = type === MODAL_TYPE.CREATE_BRAND
    const isEditMode = type === MODAL_TYPE.EDIT_BRAND

    const countryIds = useMemo(
        () =>
            Object.values(
                countries.map(country => {
                    return {
                        value: country.id,
                        label: country.id,
                    }
                }),
            ),
        [countries],
    )
    const validationSchema = useMemo(() => {
        return yup.object({
            name: yup
                .string()
                .required(t('errorMessage.validation.enterBrandName'))
                .test({
                    message: () => t('errorMessage.validation.enterBrandNameCharacterLimit'),
                    test: function (value: string): boolean {
                        if (value) {
                            const charLength = getStringLength(value)
                            return !(charLength > 300)
                        }
                        return true
                    },
                }),
            description: yup
                .string()
                .required(t('errorMessage.validation.enterBrandDescriptionCharacterLimit'))
                .test({
                    message: () => t('errorMessage.validation.enterBrandDescriptionCharacterLimit'),
                    test: function (value: string): boolean {
                        if (value) {
                            const charLength = getStringLength(value)
                            return !(charLength > 350)
                        }
                        return true
                    },
                }),

            country: yup.string().required(t('errorMessage.validation.selectCountry')),
            logo_image: yup.array().required(t('errorMessage.validation.uploadBrandLogo')),
            industry: yup.string().nullable().required(t('errorMessage.validation.selectIndustry')),
            industry_others: yup
                .string()
                .nullable()
                .test({
                    message: () => t('errorMessage.validation.enterIndustry'),
                    test: function (value) {
                        const isOtherIndustry = this.parent.industry === 'others'
                        if (isOtherIndustry) {
                            return Boolean(value)
                        } else {
                            return true
                        }
                    },
                })
                .test({
                    message: () => t('errorMessage.validation.enterIndustryCharacterLimit'),
                    test: function (value) {
                        const isOtherIndustry = this.parent.industry === 'others'
                        if (isOtherIndustry) {
                            let charLength = 0
                            if (value) {
                                charLength = getStringLength(value)
                            }
                            return charLength > 0 && charLength <= 50
                        } else {
                            return true
                        }
                    },
                }),
        })
    }, [countries, industryOptions])

    return (
        <div className={className}>
            <Modal
                size="m"
                initialValues={{
                    name: brand?.name || '',
                    description: brand?.description || '',
                    country: brand?.country || '',
                    industry: brand?.industry || null,
                    industry_others: brand?.industry_others || null,
                    member_range: brand?.member_range || null,
                    logo_image: brand?.logo_image?.public_id ? [brand.logo_image] : undefined,
                }}
                validationSchema={validationSchema}
                onSubmit={(values: any) => {
                    if (isCreateMode) {
                        dispatch(
                            createBrand({
                                teamUuid: team.uuid,
                                name: values.name,
                                description: values.description,
                                country: values.country,
                                industry: values.industry,
                                industry_others: values.industry === 'others' ? values.industry_others : null,
                                member_range: values.member_range,
                                logo_image: values.logo_image[0],
                            }),
                        )
                    } else if (isEditMode) {
                        dispatch(
                            updateBrandRequest(
                                {
                                    uuid: brand.uuid,
                                    name: values.name,
                                    description: values.description,
                                    industry: values.industry,
                                    industry_others: values.industry === 'others' ? values.industry_others : null,
                                    member_range: values.member_range,
                                    logo_image: values.logo_image[0],
                                },
                                onUpdateSuccess,
                            ),
                        )
                    }
                    onClose(true)
                }}
                onClose={onClose}
                title={isCreateMode ? t('modalWindow.createBrandTitle') : t('modalWindow.editBrandTitle')}
                submitButtonText={isCreateMode ? t('button.createBrand') : t('button.saveChanges')}
            >
                {isCreateMode ? (
                    <Modal.DropDownSelector
                        isDisabled={!countryIds}
                        options={countryIds}
                        placeholder={t('placeholderText.selectCountry')}
                        title={t('modalWindow.enterCountryHeader')}
                        name="country"
                        subtitle={t('modalWindow.enterCountrySubtext')}
                        dataTestId="test-country-selector"
                    />
                ) : (
                    <Modal.TextInput
                        width="100%"
                        name="country"
                        label={t('modalWindow.enterCountryHeader')}
                        isReadOnly
                    />
                )}

                <Modal.TextInput
                    width="100%"
                    name="name"
                    label={t('manageTeams.teamPage.brandNameTableLabel')}
                    isRequired
                    autoComplete="off"
                />
                <Modal.DropDownSelector
                    isDisabled={!industryOptions}
                    options={industryOptions}
                    placeholder={t('placeholderText.selectIndustry')}
                    title={t('modalWindow.selectIndustryHeader')}
                    name="industry"
                    customInputCondition={values => values?.industry === 'others'}
                    customInputName="industry_others"
                    isRequired
                    isSearchable
                    dataTestId="test-industry-selector"
                />

                <Modal.DropDownSelector
                    isDisabled={!memberRangeOptions}
                    options={memberRangeOptions}
                    placeholder={t('placeholderText.selectNumber')}
                    title={t('modalWindow.selectMarketingTeamSizeHeader')}
                    name="member_range"
                    dataTestId="test-member-range-selector"
                />

                <Modal.TextArea
                    width="100%"
                    name="description"
                    label={t('modalWindow.enterBrandDescriptionHeader')}
                    isRequired
                    showInputLength
                    maxInputLength={350}
                    dataTestId="test-brand-description-textarea"
                />
                <Modal.ImageUploader
                    width="120px"
                    presetType={UploadType.BRAND}
                    maxFiles={1}
                    name="logo_image"
                    title={t('modalWindow.uploadBrandLogoHeader')}
                    isRequired
                    placeholder={t('placeholderText.dragDropFiles')}
                    uploadContainerWidth="100%"
                    dataTestId="test-image-upload"
                />
            </Modal>
        </div>
    )
}

export default CreateBrand
